/*
 * Marca Scorvia (React) — símbolo oficial + nome em Inter + descritor do produto.
 * Estilos em brand/brand.css (importado também por login/login.css).
 * Arquivos de imagem servidos em /brand/ (copiados por sync-tokens.sh para public/brand/).
 *
 *   <BrandMark brand={scorviaBrand('Licitações')} />                 // fundo claro
 *   <BrandMark brand={scorviaBrand('Licitações')} variant="inverse" /> // fundo escuro
 *   <BrandMark brand={scorviaBrand('Licitações')} size="sm" />         // sidebars
 */
import type { ReactNode } from 'react'

/** Caminho público do símbolo oficial (brand/dist/symbol.png → public/brand/symbol.png). */
export const SCORVIA_SYMBOL_SRC = '/brand/symbol.png'
export const SCORVIA_NAME = 'Scorvia Tech'

export interface Brand {
  /** Símbolo (img/SVG). Para a marca Scorvia use scorviaBrand(). */
  symbol: ReactNode
  name: string
  /** Nome do produto exibido abaixo da marca (ex.: "Licitações"). */
  descriptor?: string | undefined
}

/** Marca padrão: símbolo oficial + "Scorvia Tech" + nome do produto. */
export function scorviaBrand(produto?: string): Brand {
  return {
    symbol: <img src={SCORVIA_SYMBOL_SRC} alt="" width={256} height={256} decoding="async" />,
    name: SCORVIA_NAME,
    descriptor: produto,
  }
}

export interface BrandMarkProps {
  brand: Brand
  /** "inverse" para fundos escuros (painel navy, sidebar escura). */
  variant?: 'default' | 'inverse' | undefined
  /** "sm" para sidebars e cabeçalhos compactos. */
  size?: 'md' | 'sm' | undefined
  /** Esconde o nome/descritor (ex.: sidebar recolhida); o nome vira rótulo acessível. */
  symbolOnly?: boolean | undefined
  className?: string | undefined
}

export function BrandMark({ brand, variant = 'default', size = 'md', symbolOnly = false, className }: BrandMarkProps) {
  const cls = ['ds-brand', variant === 'inverse' && 'ds-brand--inverse', size === 'sm' && 'ds-brand--sm', className]
    .filter(Boolean)
    .join(' ')
  return (
    <div className={cls} {...(symbolOnly ? { role: 'img', 'aria-label': brand.name } : {})}>
      <span className="ds-brand__symbol" aria-hidden="true">
        {brand.symbol}
      </span>
      {!symbolOnly && (
        <span className="ds-brand__text">
          <span className="ds-brand__name">{brand.name}</span>
          {brand.descriptor && <span className="ds-brand__descriptor">{brand.descriptor}</span>}
        </span>
      )}
    </div>
  )
}
